import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname } from "../../utils";
import {
  LIST_SHARING_PRIVATE,
  LIST_SHARING_SHARED,
  LIST_SHARING_PUBLIC,
} from "../../constants";

const buildPublicUrl = (listId: string) => {
  if (!listId) return "";
  if (typeof window === "undefined") return `/public/lists/${listId}`;
  return `${window.location.origin}/public/lists/${listId}`;
};

const SHARING_DESCRIPTIONS: Record<string, string> = {
  [LIST_SHARING_PRIVATE]: "Only you can view or modify this list.",
  [LIST_SHARING_SHARED]: "Anyone in your company can view and modify this list.",
  [LIST_SHARING_PUBLIC]:
    "Anyone with the link can view this list's data, no login required.",
};

export default ({
  open,
  onClose,
  list,
  getToken,
  eventListener,
}: {
  open: boolean;
  onClose: () => void;
  list: any;
  getToken: Function;
  eventListener: Function;
}) => {
  const [sharing, setSharing] = useState<string>(LIST_SHARING_PRIVATE);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (list) {
      setSharing(list.sharing || LIST_SHARING_PRIVATE);
    }
  }, [list]);

  const saveSharing = async () => {
    setSaving(true);
    try {
      const currentToken = await getToken();
      const response = await axios.put(
        `${hostname}/lists`,
        { ...list, sharing },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      if (typeof eventListener === "function") {
        eventListener({
          type: "SET_SUCCESS",
          info: response.data?.msg || "Sharing settings saved",
        });
      }
    } finally {
      setSaving(false);
    }
    onClose();
  };

  const copyPublicUrl = async () => {
    if (!list?.id) return;
    const url = buildPublicUrl(list.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (e.g. http context). The text field is
      // selectable so the user can copy manually.
    }
  };

  if (!list) {
    return <></>;
  }

  const publicUrl = list.id ? buildPublicUrl(list.id) : "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        Sharing {list.name}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormControl>
          <RadioGroup
            value={sharing}
            onChange={(e) => setSharing(e.target.value)}
          >
            <FormControlLabel
              value={LIST_SHARING_PRIVATE}
              control={<Radio />}
              label="Private"
            />
            <FormControlLabel
              value={LIST_SHARING_SHARED}
              control={<Radio />}
              label="Shared with company"
            />
            <FormControlLabel
              value={LIST_SHARING_PUBLIC}
              control={<Radio />}
              label="Public"
            />
          </RadioGroup>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {SHARING_DESCRIPTIONS[sharing]}
        </Typography>

        {sharing === LIST_SHARING_PUBLIC && publicUrl && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: "action.hover",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Public link
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <TextField
                value={publicUrl}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Tooltip title={copied ? "Copied!" : "Copy link"}>
                <IconButton onClick={copyPublicUrl} aria-label="Copy public URL">
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={saveSharing} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
